import { useRef, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, X, Save, AlertCircle, Loader2, Upload, FileText, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useRubricTemplate, useUpdateRubricTemplate, RubricCriterion, RubricKnowledgeSource } from '@/hooks/useRubricTemplates';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { useOrganisation } from '@/contexts/OrganisationContext';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { uploadAndParseDocument, deleteDocumentFromStorage } from '@/lib/documentParser';
import { AgentUserAssignment } from '@/components/admin/AgentUserAssignment';
import { toast } from 'sonner';
import { fetchTenantScopedCompanies, resolveTenantId } from '@/lib/tenantScope';

export default function EditRubricTemplate() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user, hasRole } = useAuth();
  const { tenant, isMasterTenant } = useTenant();
  const { hasEnterpriseHierarchy } = useOrganisation();
  const currentTenantId = resolveTenantId(tenant, user?.tenant_id);
  const { data: template, isLoading } = useRubricTemplate(id);
  const updateTemplate = useUpdateRubricTemplate();
  const knowledgeFileInputRef = useRef<HTMLInputElement>(null);
  
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<'patient' | 'customer' | 'coach' | 'examiner'>('patient');
  const [assignTo, setAssignTo] = useState<'all' | 'specific_companies' | 'specific_users'>('all');
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const [assignToAllUsers, setAssignToAllUsers] = useState(true);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [knowledgeSources, setKnowledgeSources] = useState<RubricKnowledgeSource[]>([]);
  const [uploadingKnowledge, setUploadingKnowledge] = useState(false);
  const [criteria, setCriteria] = useState<RubricCriterion[]>([]);

  const { data: companies = [] } = useQuery({
    queryKey: ['companies-list', currentTenantId, isMasterTenant],
    queryFn: () => fetchTenantScopedCompanies({
      tenantId: currentTenantId,
      isMasterTenant,
      select: 'id, name',
    }),
    enabled: hasRole('admin') && (isMasterTenant || !!currentTenantId)
  });

  useEffect(() => {
    if (template) {
      setName(template.name);
      setDescription(template.description || '');
      setCategory(template.category);
      setAssignTo(template.assign_to);
      setSelectedCompanyId(template.company_id);
      setKnowledgeSources(template.knowledge_sources || []);
      setSelectedUserIds(template.assigned_users || []);
      setAssignToAllUsers((template.assigned_users || []).length === 0);
      setCriteria(template.criteria || []);
    }
  }, [template]);

  const totalWeight = criteria.reduce((sum, c) => sum + c.weight, 0);

  const addCriterion = () => {
    setCriteria([...criteria, { criterion: '', weight: 0, description: '' }]);
  };

  const removeCriterion = (index: number) => {
    if (criteria.length > 2) {
      setCriteria(criteria.filter((_, i) => i !== index));
    }
  };

  const updateCriterion = (index: number, field: keyof RubricCriterion, value: string | number) => {
    const updated = [...criteria];
    updated[index] = { ...updated[index], [field]: field === 'weight' ? Number(value) || 0 : value };
    setCriteria(updated);
  };

  const handleKnowledgeFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (knowledgeSources.length + files.length > 5) {
      alert('Maximum 5 rubric knowledge files allowed');
      return;
    }

    setUploadingKnowledge(true);
    try {
      const uploaded: RubricKnowledgeSource[] = [];
      for (const file of files) {
        const result = await uploadAndParseDocument(file, 'knowledge-sources', 'rubric-templates');
        if (result.success && result.url) {
          uploaded.push({
            name: result.name,
            url: result.url,
            extracted_text: result.extracted_text,
            word_count: result.word_count,
          });
        }
      }
      if (uploaded.length > 0) {
        setKnowledgeSources(prev => [...prev, ...uploaded]);
      }
    } finally {
      setUploadingKnowledge(false);
      if (knowledgeFileInputRef.current) knowledgeFileInputRef.current.value = '';
    }
  };

  const removeKnowledgeSource = async (index: number) => {
    const fileToRemove = knowledgeSources[index];
    if (fileToRemove?.url) {
      await deleteDocumentFromStorage(fileToRemove.url, 'knowledge-sources');
    }
    setKnowledgeSources(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !name.trim()) return;
    if (assignTo !== 'all' && !selectedCompanyId) {
      toast.error(`Select a ${hasEnterpriseHierarchy ? 'branch' : 'company'} for restricted template access`);
      return;
    }
    if (assignTo === 'specific_users' && !assignToAllUsers && selectedUserIds.length === 0) {
      toast.error('Select at least one user, or assign the template to all users in the company');
      return;
    }
    
    // Company admins preserve original company_id, super admins can change
    const isAdmin = hasRole('admin');
    const finalCompanyId = isAdmin 
      ? (assignTo !== 'all' ? selectedCompanyId : null)
      : template?.company_id;
    const finalAssignTo = isAdmin
      ? (assignTo === 'specific_users' && assignToAllUsers ? 'specific_companies' : assignTo)
      : 'specific_companies';
    const finalAssignedCompanies = finalCompanyId ? [finalCompanyId] : [];
    const finalAssignedUsers = finalAssignTo === 'specific_users' ? selectedUserIds : [];
    
    await updateTemplate.mutateAsync({
      id,
      name,
      description,
      category,
      criteria,
      knowledge_sources: knowledgeSources,
      show_to_users: false,
      assign_to: finalAssignTo,
      assigned_companies: finalAssignedCompanies,
      assigned_users: finalAssignedUsers,
      company_id: finalCompanyId
    });
    
    navigate('/admin/rubric-templates');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-3xl mx-auto space-y-6">
        <div>
          <Button variant="ghost" onClick={() => navigate('/admin/rubric-templates')} className="mb-4 -ml-4">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Rubric Templates
          </Button>
          <h1 className="text-3xl font-bold text-foreground">Edit Rubric Template</h1>
          <p className="text-muted-foreground mt-1">Update evaluation criteria for agent assessments</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Template Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="name">Template Name *</Label>
                <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required />
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea id="description" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
              </div>

              <div>
                <Label>Agent Category *</Label>
                <Select value={category} onValueChange={(v: any) => setCategory(v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="patient">🏥 Patient</SelectItem>
                    <SelectItem value="customer">💼 Customer</SelectItem>
                    <SelectItem value="coach">🎓 Coach</SelectItem>
                    <SelectItem value="examiner">👨‍⚕️ Examiner</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                Evaluation Criteria
                <Badge variant={totalWeight === 100 ? 'default' : 'destructive'}>Total: {totalWeight}%</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {totalWeight !== 100 && (
                <Alert variant="destructive">
                  <AlertCircle className="w-4 h-4" />
                  <AlertDescription>Criteria weights must total 100%</AlertDescription>
                </Alert>
              )}

              {criteria.map((criterion, index) => (
                <div key={index} className="p-4 border rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Criterion {index + 1}</span>
                    {criteria.length > 2 && (
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeCriterion(index)}>
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-4 gap-3">
                    <div className="col-span-2">
                      <Label>Name *</Label>
                      <Input value={criterion.criterion} onChange={(e) => updateCriterion(index, 'criterion', e.target.value)} required />
                    </div>
                    <div>
                      <Label>Weight (%)</Label>
                      <Input type="number" min="0" max="100" value={criterion.weight} onChange={(e) => updateCriterion(index, 'weight', e.target.value)} />
                    </div>
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Input value={criterion.description} onChange={(e) => updateCriterion(index, 'description', e.target.value)} />
                  </div>
                </div>
              ))}

              <Button type="button" variant="outline" onClick={addCriterion} className="w-full">
                <Plus className="w-4 h-4 mr-2" /> Add Criterion
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Evaluation Knowledge Base</CardTitle>
              <CardDescription>Optional supporting files the evaluator should use with this rubric template</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div
                className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-primary transition-colors"
                onClick={() => knowledgeFileInputRef.current?.click()}
              >
                <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  {uploadingKnowledge ? 'Uploading and reading files...' : 'Click to upload rubric knowledge files'}
                </p>
                <p className="text-xs text-muted-foreground mt-1">Up to 5 files: PDF, TXT, DOC, DOCX</p>
                <input
                  ref={knowledgeFileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.txt,.doc,.docx"
                  onChange={handleKnowledgeFileUpload}
                  className="hidden"
                />
              </div>

              {knowledgeSources.length > 0 && (
                <div className="space-y-2">
                  {knowledgeSources.map((source, index) => (
                    <div key={`${source.url}-${index}`} className="flex items-center justify-between rounded-lg border bg-muted/40 px-3 py-2">
                      <div className="flex min-w-0 items-center gap-2">
                        <FileText className="h-4 w-4 text-primary" />
                        <span className="truncate text-sm font-medium">{source.name}</span>
                        {source.word_count ? (
                          <Badge variant="secondary" className="text-[10px]">{source.word_count} words</Badge>
                        ) : null}
                      </div>
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeKnowledgeSource(index)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Visibility & Access</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {hasRole('admin') && (
                <>
                  <div>
                    <Label>Access Control</Label>
                    <Select value={assignTo} onValueChange={(v: any) => setAssignTo(v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">🌐 All Users</SelectItem>
                        <SelectItem value="specific_companies">🏢 Specific {hasEnterpriseHierarchy ? 'Branches' : 'Companies'}</SelectItem>
                        <SelectItem value="specific_users">👥 Specific Users</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {assignTo !== 'all' && (
                    <div>
                      <Label>{hasEnterpriseHierarchy ? 'Branch' : 'Company'}</Label>
                      <Select value={selectedCompanyId || ''} onValueChange={setSelectedCompanyId}>
                        <SelectTrigger><SelectValue placeholder={`Select a ${hasEnterpriseHierarchy ? 'branch' : 'company'}...`} /></SelectTrigger>
                        <SelectContent>
                          {companies.map((company) => (
                            <SelectItem key={company.id} value={company.id}>{company.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {assignTo === 'specific_users' && (
                    <AgentUserAssignment
                      companyId={selectedCompanyId}
                      assignToAll={assignToAllUsers}
                      onAssignToAllChange={setAssignToAllUsers}
                      selectedUserIds={selectedUserIds}
                      onSelectedUsersChange={setSelectedUserIds}
                    />
                  )}
                </>
              )}
            </CardContent>
          </Card>

          <div className="flex gap-4">
            <Button type="button" variant="outline" onClick={() => navigate('/admin/rubric-templates')} className="flex-1">Cancel</Button>
            <Button type="submit" className="flex-1" disabled={updateTemplate.isPending || totalWeight !== 100}>
              <Save className="w-4 h-4 mr-2" />
              {updateTemplate.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
